import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import {
  Col,
  Row,
  Layout,
  Input,
  Card,
  Empty,
  Button,
  Table,
  Tooltip,
  Modal,
  Typography,
  message,
  Switch,
  Spin,
  Select,
  Breadcrumb,
} from "antd";
import {
  AppstoreOutlined,
  ExclamationCircleFilled,
  BarsOutlined,
  DeleteOutlined,
  EditOutlined,
  FileSearchOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import "../newsMainpage/styles/index.less";
import type { TableProps } from "antd";
import dayjs from "dayjs";
import "dayjs/locale/th";
import buddhistEra from "dayjs/plugin/buddhistEra";
import { useAuth } from "../../utils/Auth/AuthWrapper";
import { useNavigate } from "react-router-dom";
import {
  get_active_news,
  get_all_news,
  news_delete,
  news_update_status,
} from "../../services/news";
import { useTranslation } from "react-i18next";

dayjs.extend(buddhistEra);
dayjs.locale("th");

const { Content } = Layout;
const { Search } = Input;
const { confirm } = Modal;
const { Paragraph } = Typography;

interface NewsItem {
  key: string;
  id: string;
  news_id: string;
  img: string;
  title: string;
  created_date: string;
  lasted_update: string;
  status?: number;
}

type ViewType = "table" | "card";
type ViewMode = "admin" | "user";

// ✅ Constants
const ITEMS_PER_PAGE = 10;
const LOAD_MORE_DELAY = 500;
const SEARCH_DEBOUNCE_DELAY = 300;
const SUPER_ADMIN_ID = "99999999-0000-0000-0000-999999999999";

// ✅ Custom Hook: Debounced Search
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

// ✅ Custom Hook: Infinite Scroll
const useInfiniteScroll = (
  enabled: boolean,
  totalItems: number,
  onLoadMore: () => void
) => {
  const loaderRef = useRef<HTMLDivElement>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && !isLoadingMore) {
          setIsLoadingMore(true);
          setTimeout(() => {
            onLoadMore();
            setIsLoadingMore(false);
          }, LOAD_MORE_DELAY);
        }
      },
      { threshold: 1.0, rootMargin: "100px" }
    );

    const currentRef = loaderRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      observer.disconnect();
    };
  }, [enabled, isLoadingMore, onLoadMore]);

  return { loaderRef, isLoadingMore };
};

// ✅ Utility Functions
const formatDate = (date: string): string => {
  return dayjs(date).format("DD MMM BBBB");
};

const getImageUrl = (img: string): string => {
  if (!img) return "";
  return img.startsWith("/data/")
    ? `${process.env.REACT_APP_API}${img}`
    : img;
};

const Newsmainpage: React.FC = () => {
  const auth = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // ✅ States - Consolidated
  const isSuperAdmin = auth.user.role === "Super Admin";

  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [view, setView] = useState<ViewType>(isSuperAdmin ? "table" : "card");
  const [viewMode, setViewMode] = useState<ViewMode>(isSuperAdmin ? "admin" : "user");
  const [allNews, setAllNews] = useState<NewsItem[]>([]);
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);

  const debouncedSearch = useDebounce(searchText, SEARCH_DEBOUNCE_DELAY);

  // ✅ Fetch Data - Optimized
  const fetchAllNews = useCallback(async (searchTerm = "") => {
    try {
      const data = {
        fields: ["*"],
        filter: {
          ...(searchTerm && { title: searchTerm })
        }
      };
      const res = await get_all_news(data);
      return res.result || [];
    } catch (error) {
      console.error("Error fetching all news:", error);
      message.error(t("Unable to display news data"));
      return [];
    }
  }, [t]);

  const fetchActiveNews = useCallback(async (searchTerm = "") => {
    try {
      const data = {
        fields: ["*"],
        filter: {
          ...(searchTerm && { title: searchTerm })
        }
      };
      const res = await get_active_news(data);
      return res.result || [];
    } catch (error) {
      console.error("Error fetching active news:", error);
      message.error(t("Unable to display news data"));
      return [];
    }
  }, [t]);

  const loadNews = useCallback(async (searchTerm = "") => {
    setIsLoading(true);
    try {
      const news = await (viewMode === "admin"
        ? fetchAllNews(searchTerm)
        : fetchActiveNews(searchTerm));
      setAllNews(news);
    } finally {
      setIsLoading(false);
    }
  }, [viewMode, fetchAllNews, fetchActiveNews]);

  useEffect(() => {
    loadNews(debouncedSearch);
    setVisibleCount(ITEMS_PER_PAGE); // Reset pagination
  }, [viewMode, debouncedSearch, loadNews]);
  const filteredData = useMemo(() => {
    return allNews.map((item) => ({
      ...item,
      key: item.news_id || item.id,
    }));
  }, [allNews]);

  // ✅ Handle Status Change
  const handleChangeStatus = useCallback(async (record: NewsItem, value: boolean) => {
    try {
      await news_update_status({
        status: value ? 1 : 2,
        news_id: record.news_id,
      });
      
      // ✅ Optimistic Update
      setAllNews((prev) =>
        prev.map((item) =>
          item.news_id === record.news_id
            ? { ...item, status: value ? 1 : 2 }
            : item
        )
      );
      
      message.success(t("Status updated successfully"));
    } catch (error) {
      console.error("Error updating status:", error);
      message.error(t("Failed to update status"));
    }
  }, [t]);

  // ✅ Handle Delete
  const handleDelete = useCallback(async (newsId: string) => {
    try {
      const res = await news_delete({ news_id: newsId });
      if (res.status === "success") {
        message.success(t("Delete Success!"));
        setAllNews((prev) => prev.filter((item) => item.news_id !== newsId));
      }
    } catch (error) {
      console.error("Error deleting news:", error);
      message.error(t("Failed to delete news"));
    }
  }, [t]);

  const showDeleteConfirm = useCallback((id: string, eventName: string) => {
    confirm({
      title: t("delete title"),
      icon: <ExclamationCircleFilled style={{ color: "#FF3B30" }} />,
      content: (
        <div style={{ textAlign: "center" }}>
          <p>{t("You want to delete the news")} :</p>
          <p><strong>{eventName}</strong></p>
          <p>{t("yes or no")}</p>
        </div>
      ),
      okText: t("confirm"),
      okType: "danger",
      cancelText: t("cancel"),
      onOk: () => handleDelete(id),
    });
  }, [t, handleDelete]);

  // ✅ Table Columns - Memoized
  const columns: TableProps<NewsItem>["columns"] = useMemo(
    () => [
      {
        title: <div style={{ fontSize: "18px", textAlign: "center" }}>{t("Name News")}</div>,
        dataIndex: "title",
        key: "title",
        width: 700
      },
      {
        title: <div style={{ fontSize: "18px", textAlign: "center" }}>{t("News Date")}</div>,
        key: "dates",
        align: "center",
        render: (_: any, record: NewsItem) => (
          <div style={{ lineHeight: "1.8" }}>
            <div>{formatDate(record.created_date)}</div>
            <div style={{ color: "#888" }}>{formatDate(record.lasted_update)}</div>
          </div>
        ),
      },
      {
        title: <div style={{ fontSize: "18px", textAlign: "center" }}>{t("Active")}</div>,
        dataIndex: "status",
        key: "status",
        align: "center",
        render: (_: any, record: NewsItem) => (
          <Switch
            checked={record.status === 1}
            checkedChildren="เปิด"
            unCheckedChildren="ปิด"
            onChange={(value) => handleChangeStatus(record, value)}
          />
        ),
      },
      {
        title: "",
        key: "action",
        render: (_: any, record: NewsItem) => (
          <Row justify="center" align="middle">
            <Tooltip title="Detail">
              <Button
                style={{ backgroundColor: "#d1d7fa" }}
                icon={<FileSearchOutlined style={{ color: "#615EFC" }} />}
                onClick={() => navigate(`/news/detail/${record.news_id}`)}
              />
            </Tooltip>

            {isSuperAdmin && (
              <>
                <Tooltip title="Edit">
                  <Button
                    style={{ backgroundColor: "#fff4cc", marginLeft: "10px" }}
                    icon={<EditOutlined style={{ color: "#FFB84C" }} />}
                    onClick={() => navigate(`/news/edit/${record.news_id}`)}
                  />
                </Tooltip>

                <Tooltip title="Delete">
                  <Button
                    style={{ backgroundColor: "#FFDFDF", marginLeft: "10px" }}
                    icon={<DeleteOutlined style={{ color: "#B31312" }} />}
                    onClick={() => {
                      if (record.id === SUPER_ADMIN_ID) {
                        Modal.error({
                          title: "Permission Denied!",
                          content: "Not allowed to delete Super Admin's data.",
                        });
                      } else {
                        showDeleteConfirm(record.news_id, record.title);
                      }
                    }}
                  />
                </Tooltip>
              </>
            )}
          </Row>
        ),
      },
    ],
    [t, isSuperAdmin, handleChangeStatus, navigate, showDeleteConfirm]
  );

  // ✅ Infinite Scroll Hook
  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + ITEMS_PER_PAGE, filteredData.length));
  }, [filteredData.length]);

  const { loaderRef, isLoadingMore } = useInfiniteScroll(
    view === "card" && visibleCount < filteredData.length,
    filteredData.length,
    handleLoadMore
  );

  // ✅ Render Card View
  const renderCardView = useMemo(() => {
    if (filteredData.length === 0) {
      return (
        <Row justify="center" align="middle" style={{ minHeight: "50vh" }}>
          <Empty description={t("No data")} />
        </Row>
      );
    }

    const visibleItems = filteredData.slice(0, visibleCount);
    const featuredItems = visibleItems.slice(0, 5);
    const remainingItems = visibleItems.slice(5);

    return (
      <>
        {/* Featured section - first 4 cards */}
        {featuredItems.length > 0 && (
          <Row gutter={[16, 16]} className="featured-row">
            {/* Hero card - first card */}
            <Col xs={24} md={16} className="featured-hero-col">
              <Card
                hoverable
                className="card-global card-hero"
                onClick={() => navigate(`/news/detail/${featuredItems[0].news_id}`)}
              >
                <div className="hero-image-container">
                  <span className="news-tag">ข่าวสาร</span>
                  <img
                    alt={featuredItems[0].title}
                    src={getImageUrl(featuredItems[0].img)}
                    className="hero-image"
                    loading="lazy"
                  />
                  <div className="hero-overlay">
                    <span className="hero-date">{formatDate(featuredItems[0].created_date)}</span>
                    <strong className="hero-title">{featuredItems[0].title}</strong>
                  </div>
                </div>
              </Card>
            </Col>

            {/* Side cards - cards 2-4 */}
            {featuredItems.length > 1 && (
              <Col xs={24} md={8} className="featured-side-col">
                <div style={{ display: "flex", flexDirection: "column", gap: 16, height: "100%" }}>
                  {featuredItems.slice(1).map((card) => (
                    <Card
                      key={card.key}
                      hoverable
                      className="card-global card-side"
                      onClick={() => navigate(`/news/detail/${card.news_id}`)}
                    >
                      <Row align="middle">
                        <Col flex="80px">
                          <div className="side-image-wrapper">
                            <img
                              alt={card.title}
                              src={getImageUrl(card.img)}
                              className="side-image"
                              loading="lazy"
                            />
                          </div>
                        </Col>
                        <Col flex="auto" style={{ paddingLeft: 12 }}>
                          <Paragraph className="side-card-title">
                            <strong>{card.title}</strong>
                          </Paragraph>
                          <span className="side-card-date">{formatDate(card.created_date)}</span>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                </div>
              </Col>
            )}
          </Row>
        )}

        {/* Regular grid - remaining cards (5+) */}
        {remainingItems.length > 0 && (
          <Row gutter={[16, 20]}>
            {remainingItems.map((card) => (
              <Col xs={24} sm={12} md={8} key={card.key}>
                <Card
                  hoverable
                  className="card-global card-regular"
                  onClick={() => navigate(`/news/detail/${card.news_id}`)}
                >
                  <div className="regular-image-wrapper">
                    <span className="news-tag">ข่าวสาร</span>
                    <img
                      alt={card.title}
                      src={getImageUrl(card.img)}
                      className="regular-image"
                      loading="lazy"
                    />
                  </div>
                  <div className="regular-content">
                    <Paragraph className="card-title">
                      <strong>{card.title}</strong>
                    </Paragraph>
                    <span className="card-meta">
                      {formatDate(card.created_date)}
                    </span>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        {isLoadingMore && (
          <Row justify="center" style={{ marginTop: 20 }}>
            <Spin />
          </Row>
        )}

        <div ref={loaderRef} style={{ height: "10px" }} />
      </>
    );
  }, [filteredData, visibleCount, isLoadingMore, loaderRef, navigate, t]);

  return (
    <Content className="news-layout">
      {/* Header */}
      <div>
        <Row justify="space-between" align="middle">
          <Col xs={24} md={16}>
            <Breadcrumb
              separator=">"
              items={[{ title: <span style={{ fontWeight: "bold" }}>{t("News")}</span> }]}
            />
          </Col>

          <Col xs={24} md={8}>
            <Row justify="end" gutter={[10, 10]}>
              <Col xs={24} sm={24} md={14} lg={12}>
                <Search
                  placeholder={t("Search")}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  allowClear
                />
              </Col>

              {isSuperAdmin && (
                <Col>
                  <Button
                    style={{ backgroundColor: "#00215E", color: "white" }}
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => navigate("/news/create")}
                  >
                    {t("Add News")}
                  </Button>
                </Col>
              )}
            </Row>
          </Col>
        </Row>
        <hr />
      </div>

      {/* View Controls */}
      {isSuperAdmin && (
        <Row gutter={8} align="middle" justify="end" style={{ marginBottom: "0.4rem" }}>
          <Col>{t("View mode")} : </Col>
          <Col>
            <Select
              style={{ width: "120px" }}
              value={viewMode}
              onChange={(value: ViewMode) => {
                setViewMode(value);
                setView(value === "admin" ? "table" : "card");
              }}
              options={[
                { label: "Admin", value: "admin" },
                { label: "User", value: "user" },
              ]}
            />
          </Col>
          {viewMode === "admin" && (
            <>
              <Col>
                <Button
                  style={{
                    borderColor: "#91caff",
                    backgroundColor: view === "table" ? "#e6f4ff" : "#ffffff",
                  }}
                  icon={<BarsOutlined style={{ color: "#0958d9" }} />}
                  onClick={() => setView("table")}
                />
              </Col>
              <Col>
                <Button
                  style={{
                    borderColor: "#91caff",
                    backgroundColor: view === "card" ? "#e6f4ff" : "#ffffff",
                  }}
                  icon={<AppstoreOutlined style={{ color: "#0958d9" }} />}
                  onClick={() => setView("card")}
                />
              </Col>
            </>
          )}
        </Row>
      )}

      {/* Content */}
      {isLoading ? (
        <Row justify="center" align="middle" style={{ height: "50vh" }}>
          <Spin size="large" />
        </Row>
      ) : view === "table" ? (
        <Table
          className="custom-table-news"
          columns={columns}
          dataSource={filteredData}
          pagination={{
            showTotal: (total, range) =>
              `แสดงรายการต่อหน้า : ${range[0]}-${range[1]} จาก ${total} รายการ`,
            defaultPageSize: ITEMS_PER_PAGE,
            showSizeChanger: false,
          }}
          scroll={{ x: "max-content" }}
        />
      ) : (
        renderCardView
      )}
    </Content>
  );
};

export default Newsmainpage;